import { NGXLogger } from 'ngx-logger';
import { Injectable } from '@angular/core';
import { CommsService } from '@services/comms/comms.service';
import { ICheck, IResponse, IResponseCollection, Response } from '@modules/management/pages/details/check/models';
import { DeploymentService } from '@modules/management/pages/details/check/services/deployment/deployment.service';
import { AssetsService } from '@services/assets/assets.service';
import { UserService } from '@services/user/user.service';
import { AccountsService } from '@services/accounts/accounts.service';
import { Events } from '@services/events/events.service';
import { UserdataService } from '@services/userdata/userdata.service';
import { ChecksService } from '@services/checks/checks.service';
import { UtilsService } from '@services/utils/utils.service';
import { TeamsService } from '@services/teams/teams.service';
import { TranslateService } from '@ngx-translate/core';
import { Permission, PermissionsService } from '@services/permissions/permissions.service';
import { awaitHandler } from '@utils/awaitHandler';
import { IObjectStringKeyMap } from '@modules/shared/models';
import { LocalDBService } from '@services/localdb.service.ts/localdb';
import { IDBUpdate, IUpdateTime } from 'app/db.consts';
import { WebWorkerService } from '@services/web-worker/web-worker.service';
import {
  assign,
  clone, cloneDeep,
  each,
  filter,
  find, flattenDeep,
  forEach,
  get,
  has,
  includes,
  indexOf,
  intersection,
  isEmpty, keyBy,
  keys,
  map,
  memoize, some,
  sortBy,
  split,
  toLower,
  values
} from 'lodash';
import { FoldersDataService } from '@services/folders/folders-data.service';

import * as moment from 'moment-timezone';
import { FolderDataType } from '@modules/management/modules/folders/model/folders.interfaces';
import { BehaviorSubject } from 'rxjs';

export interface IReportProgress {
  pageNum: number;
  totalIDs: number;
  hasMore: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CheckResponseService {

  private responses: { data: IObjectStringKeyMap<IResponse>; lastRequest: number } = {
    data: null,
    lastRequest: null
  };

  public responseFilterObject: any = {};
  public lastStorageTime = 0;
  public reportProgress$ = new BehaviorSubject<IReportProgress | null>(null);
  public reportIntervalCache: { cacheKey: string; result: any } | null = null;

  private responseCacheData: {
    startTime: number;
    endTime: number;
  } = {startTime: null, endTime: null};

  private responseItemCache: any = {};

  private initialCacheFill = false;
  private partialLoad = null;
  private originalLastRequest = null;

  private updatingCounter = 0;
  private refreshing = false;
  private lastChunkSize = 0;
  private chunkSize = 50000;
  private getMatchingCallId = 0;

  private upToDate = false;

  constructor(
    private logger: NGXLogger,
    private commsService: CommsService,
    private deployment: DeploymentService,
    private assetsService: AssetsService,
    private userService: UserService,
    private userDataService: UserdataService,
    private accountsService: AccountsService,
    private events: Events,
    private utils: UtilsService,
    private checkService: ChecksService,
    private translate: TranslateService,
    private teamsService: TeamsService,
    private permissions: PermissionsService,
    private localdb: LocalDBService,
    private webworkers: WebWorkerService,
    private foldersDataService: FoldersDataService
  ) {
  }

  public async refresh(updated: number = 0): Promise<boolean> {
    if (this.responses && this.responses?.data && this.responses?.lastRequest) {
      if (updated && updated < this.responses.lastRequest) {
        this.logger.log(`local response cache already up to date: ${updated}, ${this.responses.lastRequest}`);
        return Promise.resolve(true);
      }
      // new logic - only do a refresh if we have some responses cached.  This will keep the
      // local collection up to date.
      return new Promise(async (resolve, reject) => {
        const r = {
          locations: JSON.stringify(this.userDataService.locations),
          status: JSON.stringify(['available', 'inProgress', 'complete', 'groupsCompleted', 'skipped', 'notCompleted', 'missed' ]),
          trim: 1,
          lastRequest: this.responses.lastRequest,
          changesOnly: 1
        };
        await this.getResponses(r, true, true, false);
        resolve(true);
      });
    // } else {
    //   this.logger.debug('attempting a local restore');
    //   return this.restoreFromLocalDB();
    }
  }

  public async updateCache(data, honorLastRequest: boolean = true) {
    // is this an incremental update?
    const responses = values(data.result.responses);

    if (this.responses.data && this.responses.lastRequest) {
      if (has(data.result, 'removed')) {
        if (has(data.result.removed, 'abandoned')) {
          each(data.result.removed.abandoned, (item) => {
            delete this.responses.data[item];
          });
          this.deleteFromLocalDB(data.result.removed.abandoned);
        }
        if (has(data.result.removed, 'canceled')) {
          each(data.result.removed.canceled, (item) => {
            delete this.responses.data[item];
          });
          this.deleteFromLocalDB(data.result.removed.canceled);
        }
      }
      await this.utils.iterateWithDelay(responses, (item) => {
        this.responses.data[item.responseID] = item;
      });
      if (honorLastRequest) {
        this.responses.lastRequest = data.result.timestamp;
      }
    } else {
      if (honorLastRequest) {
        this.responses.lastRequest = data.result.timestamp;
      }
      // await this.utils.iterateWithDelay(responses, (item) => {
      //   const deployment = this.deployment.getDeployment(item.deploymentID);
      //   const check = this.checkService.getCheck(deployment.checkID) || <ICheck>{};

      //   item = defaults(item, deployment);
      //   item.checkType = check.checkType;
      //   item.deploymentAssignedTo = deployment.assignedTo;
      // });
      this.responses.data = data.result.responses;
    }
    this.logger.log('Now we have ' + keys(this.responses.data).length + ' responses cached');
    if (this.updatingCounter) {
      this.updatingCounter--;
      if (this.updatingCounter) {
        this.logger.log(`There are still ${this.updatingCounter} pending updates`);
      }
    }

    // if (updateStorage && !this.updatingCounter) {
    //   if (!this.lastStorageTime || this.responses.lastRequest > this.lastStorageTime + 60 * 1000 * 1000) {
    //     this.lastStorageTime = this.responses.lastRequest;
    //     this.storageService.store('responses', this.responses, 'data', 'responseID', 50000);
    //   } else {
    //     this.logger.log('too soon for cache update');
    //   }
    // }
  }

  public clearCache() {
    this.upToDate = false;
    this.responses.data = null;
    this.responses.lastRequest = null;
    this.lastStorageTime = 0;
    this.originalLastRequest = null;
    this.partialLoad = null;
    this.responseItemCache = {};
    this.updatingCounter = 0;
    this.responseCacheData.startTime = null;
    this.reportIntervalCache = null;
  }

  public async getResponseAsync(id: string): Promise<Response | null> {
    return new Promise((resolve) => {
      let m = find(this.responses.data, {responseID: id}) as IResponse | null;
      if (m) {
        const dep = this.deployment.getDeployment(m.deploymentID);
        resolve(new Response(m, this.checkService.getCheck(dep.checkID), dep));
      } else {
        this.getResponses({responses: JSON.stringify([id]), trim: 1, status: '[]', changesOnly: true})
          .then((data) => {

            m = find(data, {responseID: id}) as IResponse | null;
            if (m) {
              if (!this.responses.data) {
                this.responses.data = {};
              }
              this.responses.data[m.responseID] = m;
              const dep = this.deployment.getDeployment(m.deploymentID);
              resolve(new Response(m, this.checkService.getCheck(dep.checkID), dep));
            }
          }).catch(err => {
          resolve(null);
        });
      }
    });
  }

  public async getDeploymentByResponseID(rid: string): Promise<string> {
    const respObj: any = await this.getResponseAsync(rid);
    const deployment: any = this.deployment.getDeployment(respObj.deploymentID);
    if (deployment) {
      return deployment.title;
    } else {
      return '';
    }
  }

  public getDeploymentByID(did: string): string {
    const deployment: any = this.deployment.getDeployment(did);
    if (deployment) {
      return deployment.title;
    } else {
      return '';
    }
  }

  public async getResponseByObservationID(oid: number) {
    const ridObj: any = this.findRespItemByObsID(oid);
    if (!ridObj) {
      return null;
    }

    const respObj: any = await this.getResponseAsync(ridObj.responseID);
    if (!respObj) {
      return null;
    }
    const deployment: any = this.deployment.getDeployment(respObj.deploymentID);
    const splitSig = split(respObj.targetSignature, ':');
    // Translate these stuff
    let targStr = '';
    if (splitSig[0] === 'loc') {
      targStr = 'Zone' + ': ' + this.userService.findAnyZoneNoLoc(splitSig[2]).name;
    } else if (splitSig[0] === 'asset') {
      const targObj = this.assetsService.getAssetById(+splitSig[1]);
      targStr = 'Asset' + ': ' + targObj[0].name;
    } else if (splitSig[0] === 'worker') {
      targStr = 'Worker' + ': ' + this.accountsService.fullname(+splitSig[1]);
    }
    ridObj.checkID = respObj.checkID;
    ridObj.owner = respObj.owner;
    ridObj.targetSignature = targStr;
    ridObj.deployment = deployment ? deployment.title : '';
    return ridObj;
  }


  public getAssigneeNames(assignedTo: any, deployment?: any, dereferenceSupervisors?: boolean, targetSignature?: string): string {
    let assigned = '';
    let separate = false;

    if (get(assignedTo, 'teams.length')) {
      const teams: string[] = [];
      assignedTo.teams.forEach((teamID) => {
        if (teamID === -1) {
          teams.push(this.translate.instant('MGMT_DETAILS.Workers_Primary_Team'));
        } else {
          teams.push(this.teamsService.teamNameByID(teamID));
        }
      });
      if (assignedTo.teams.length === 1) {
        assigned += this.translate.instant('SHARED.Team') + ': ' + map(teams).join(', ');
      } else {
        assigned += this.translate.instant('SHARED.TEAMS') + ': ' + map(teams).join(', ');
      }
      separate = true;
    } else if (assignedTo.teams || (deployment && deployment.target.targetType === 'workers')) {
      assigned += separate ? '<hr>' : '';
      assigned += this.translate.instant('SHARED.TEAMS') + ': ' + this.translate.instant('SHARED.Any_Team');
      separate = true;
    }

    if (assignedTo.users && assignedTo.users.length) {
      const users: string[] = [];
      assignedTo.users.forEach((userID) => {
        if (userID === -1) {
          users.push(this.translate.instant('MGMT_DETAILS.Self_Check'));
        } else if (userID === -2) {
          if (dereferenceSupervisors && targetSignature && targetSignature.match(/^worker/)) {
            const t = targetSignature.split(/:/);
            const u = this.accountsService.getAccount(+t[1]);
            if (u) {
              if (u.supervisorID) {
                users.push(this.userService.getFullname(u.supervisorID));
              } else {
                users.push(this.translate.instant('MGMT_DETAILS.User_Supervisor'));
              }
            } else {
              users.push(this.translate.instant('MGMT_DETAILS.User_Supervisor'));
            }
          } else {
            users.push(this.translate.instant('MGMT_DETAILS.User_Supervisor'));
          }
        } else {
          users.push(this.userService.getFullname(userID));
        }
      });
      assigned += separate ? '<hr>' : '';
      if (users.length === 1) {
        assigned += this.translate.instant('SHARED.USER') + ': ' + map(users).join(', ');
      } else {
        assigned += this.translate.instant('SHARED.USERS') + ': ' + map(users).join(', ');
      }
      separate = true;
    }

    if (get(assignedTo, 'permissions.length')) {
      const permissions: string[] = [];
      assignedTo.permissions.forEach((permID) => {
        const perm: any = find(this.permissions.permissions.data, {id: permID});
        permissions.push(this.translate.instant(perm.description));
      });
      assigned += separate ? '<hr>' : '';
      if (assignedTo.permissions.length === 1) {
        assigned += this.translate.instant('MGMT_LIST.Permission') + ': ' + map(permissions).join(', ');
      } else {
        assigned += this.translate.instant('SHARED.EDIT_Permissions') + ': ' + map(permissions).join(', ');
      }
    } else if (assignedTo.permissions || (deployment && deployment.target.targetType === 'workers')) {
      assigned += separate ? '<hr>' : '';
      assigned += this.translate.instant('SHARED.EDIT_Permissions') + ': ' + this.translate.instant('SHARED.Any_Permissions');
    }
    return assigned || this.translate.instant('SHARED.None');
  }

  public getTargetInfo(targetSignature) {
    if (!targetSignature) {
      return;
    }
    const splitSig = split(targetSignature, ':');
    // Translate these stuff
    let targStr = '';
    if (splitSig[0] === 'loc') {
      targStr = this.getTargetZoneName(+splitSig[1], +splitSig[2]);
    } else if (splitSig[0] === 'asset') {
      const targObj = this.assetsService.getAssetById(+splitSig[1]);
      targStr = this.translate.instant('SHARED.Asset') + ': ' + toLower(targObj[0].name);
    } else if (splitSig[0] === 'worker') {
      targStr = this.translate.instant('SHARED.Worker') + ': ' + toLower(this.accountsService.getCompactName(+splitSig[1]));
    }
    return targStr;
  }

  public async getMatchingCheckResponses(filters: any): Promise<IResponseCollection> {
    /*
    logic: marshal filters into getResponseIDs requests (paginated).
    For each page of responseIDs returned:
      - items already in memory and up to date: add directly to result
      - items in IndexedDB cache and up to date: load from cache
      - remaining items: fetch from backend via getResponsesByRow
    Each page is processed immediately as it arrives — no accumulation of
    the full ID set before processing begins.

    Concurrency: as soon as page N's ID list arrives and we know there is a
    page N+1, we fire the getResponseIDs call for page N+1 AND — once those
    IDs resolve — immediately begin the loadFromCache + getResponsesByRow
    pipeline for page N+1 concurrently with page N's own pipeline.  The
    localdb write queue (queueRunner) already serialises IDB writes, so
    concurrent data fetches are safe.
    */

    const f = cloneDeep(filters);
    if (!has(f, 'startTime')) {
      throw new Error('startTime is a required parameter');
    }
    f.startTime = this.utils.toSeconds(f.startTime);
    if (has(f, 'endTime')) {
      f.endTime = this.utils.toSeconds(f.endTime);
    }

    if (f?.locationTime) {
      // we are using the location time - get the timezone
      f.tz = moment.tz.guess();
    }

    if (!has(f, 'checkStatus') || f.checkStatus.length === 0) {
      f.checkStatus = ['available', 'inProgress', 'complete', 'groupsCompleted', 'skipped', 'notCompleted', 'missed' ];
    }

    if (!has(f, 'states') || f.states.length === 0) {
      f.states = ['available', 'inProgress', 'complete', 'groupsCompleted', 'skipped', 'notCompleted', 'missed' ];
    }

    const msg: any = {
      cmd: 'getResponseIDs',
      // CVX-5929: honor a caller-supplied useUpdateTime (0 = filter by
      // availableTime/scheduled day, 1 = filter by updated_at/last activity).
      // Defaults to 1 so callers that don't set it keep the original behavior.
      selectors: JSON.stringify(f),
      useUpdateTime: has(f, 'useUpdateTime') ? f.useUpdateTime : 1
    };

    // Helper: given a resolved page of IDs, run loadFromCache + getResponsesByRow and return
    // the full pageMatches array (so the consumer can copy hits from this.responses.data).
    // This is the data pipeline for one page, designed to be run concurrently with other pages.
    const runDataPipeline = (
      pageMatches: Array<{responseID: string; updatedAt: number; row: number}>
    ): Promise<Array<{responseID: string; updatedAt: number; row: number}>> => {
      // Split in-memory hits from misses
      const needed = [];
      each(pageMatches, item => {
        if (!(has(this.responses.data, item.responseID) && item.updatedAt <= this.responses.data[item.responseID].updatedAt)) {
          needed.push(item);
        }
      });
      if (!needed.length) {
        return Promise.resolve(pageMatches);
      }
      return this.loadFromCache(needed).then(missed => {
        if (!missed?.length) {
          return pageMatches;
        }
        const rows = missed.map(item => item.row);
        return awaitHandler(this.getResponsesByRow(rows)).then(([, pipelineErr]) => {
          if (pipelineErr) {
            this.logger.error(`getMatchingCheckResponses: getResponsesByRow failed: ${pipelineErr}`);
          }
          return pageMatches;
        });
      });
    };

    const callId = ++this.getMatchingCallId;
    const responses = {};
    let lastResponse: number | null = null;
    let pageNum = 0;
    let complete = false;

    // nextPageIdsPromise: pre-fetched [data, err] pair for the next page's getResponseIDs call.
    let nextPageIdsPromise: Promise<[any, any]> | null = null;

    // pendingNextPageIdsPromise: getResponseIDs call for page N+2, fired inside the .then()
    // callback when page N+1's IDs arrive.  Promoted to nextPageIdsPromise at the start of the
    // iteration that processes page N+1, so page N+2's IDs fetch runs concurrently with page
    // N+1's data pipeline instead of waiting for page N's data pipeline to finish.
    let pendingNextPageIdsPromise: Promise<[any, any]> | null = null;

    // thisPageDataPromise: the in-flight data pipeline for the page we are currently processing,
    // started concurrently during the PREVIOUS iteration as a prefetch.
    // null for page 1 (nothing was prefetched yet) or when prefetch is unavailable.
    let thisPageDataPromise: Promise<Array<{responseID: string; updatedAt: number; row: number}>> | null = null;

    this.logger.debug(`getMatchingCheckResponses: starting windowed retrieval (request ${callId})`);
    this.reportProgress$.next({ pageNum: 0, totalIDs: 0, hasMore: false });

    while (!complete) {
      if (callId !== this.getMatchingCallId) {
        this.logger.debug(`getMatchingCheckResponses: request ${callId} superseded, aborting`);
        return this.buildClasses({});
      }

      // Consume prefetched promise (pages 2+) or make a fresh fetch (page 1)
      let data: any;
      let err: any;
      if (nextPageIdsPromise) {
        [data, err] = await nextPageIdsPromise;
        nextPageIdsPromise = null;
      } else {
        const e = clone(msg);
        e.limit = this.chunkSize;
        delete e.startAfter;
        [data, err] = await awaitHandler(this.commsService.sendMessage(e));
      }

      if (err) {
        const errMsg = err instanceof Error ? err.message : JSON.stringify(err);
        this.logger.error(`getMatchingCheckResponses: getResponseIDs page ${pageNum + 1} failed: ${errMsg}`);
        throw new Error(err);
      }

      if (data && data.reqStatus === 'OK') {
        pageNum++;
        // Determine pagination state before emitting progress so hasMore is accurate
        const isLastPage = !data.result.numResponses || data.result.numResponses === 0 || data.result.numResponses < this.chunkSize;
        this.reportProgress$.next({ pageNum, totalIDs: Object.keys(responses).length + (data.result.responses?.length ?? 0), hasMore: !isLastPage });
        const pageMatches: Array<{responseID: string; updatedAt: number; row: number}> = data.result.responses || [];
        this.logger.debug(`getMatchingCheckResponses: page ${pageNum} — ${data.result.numResponses} IDs; processing immediately`);

        // Grab the prefetched pipeline for THIS page (started in the previous iteration).
        // May be null for page 1 or if the previous prefetch was unavailable.
        const myDataPromise = thisPageDataPromise;
        thisPageDataPromise = null;

        if (!isLastPage) {
          // Kick off next page's getResponseIDs fetch NOW, before waiting for myDataPromise.
          // If pendingNextPageIdsPromise was already fired from inside a previous .then() callback,
          // reuse it; otherwise fire a fresh request.
          lastResponse = data.result.lastResponse;
          let rawNextIdsPromise: Promise<[any, any]>;
          if (pendingNextPageIdsPromise) {
            // Already in-flight — pick it up (fired when the previous page's IDs arrived).
            rawNextIdsPromise = pendingNextPageIdsPromise;
            pendingNextPageIdsPromise = null;
            this.logger.debug(`getMatchingCheckResponses: reusing pre-fired IDs promise for page ${pageNum + 1}`);
          } else {
            const nextMsg = clone(msg);
            nextMsg.limit = this.chunkSize;
            nextMsg.startAfter = lastResponse;
            this.logger.debug(`getMatchingCheckResponses: firing IDs request for page ${pageNum + 1}`);
            rawNextIdsPromise = awaitHandler(this.commsService.sendMessage(nextMsg));
          }
          nextPageIdsPromise = rawNextIdsPromise;

          // As soon as the next page's IDs arrive, immediately start its data pipeline so it
          // runs concurrently with the current page's data work below (myDataPromise / inline).
          // Also, if the next page itself is not the last, immediately fire getResponseIDs for
          // the page after that (page N+2) so its network round-trip overlaps with the data
          // pipeline — eliminating the 34-second cascade delay for pages 3, 4, 5, …
          // Store the result in thisPageDataPromise for the next iteration to consume.
          thisPageDataPromise = rawNextIdsPromise.then(([nextData, nextErr]) => {
            if (nextErr || !nextData || nextData.reqStatus !== 'OK') {
              this.logger.debug(`getMatchingCheckResponses: prefetch of next page IDs failed — next iteration will run inline`);
              return null;
            }
            const nextMatches: Array<{responseID: string; updatedAt: number; row: number}> = nextData.result.responses || [];
            const nextIsLastPage = !nextData.result.numResponses || nextData.result.numResponses === 0 || nextData.result.numResponses < this.chunkSize;
            // Pre-fire getResponseIDs for page N+2 as soon as page N+1's IDs arrive, so the
            // network round-trip for page N+2 overlaps with page N+1's (and page N's) data
            // pipeline.  Only fire if not cancelled and there is a next page.
            if (!nextIsLastPage && callId === this.getMatchingCallId) {
              const nextNextMsg = clone(msg);
              nextNextMsg.limit = this.chunkSize;
              nextNextMsg.startAfter = nextData.result.lastResponse;
              this.logger.debug(`getMatchingCheckResponses: pre-firing IDs request for page ${pageNum + 2}`);
              pendingNextPageIdsPromise = awaitHandler(this.commsService.sendMessage(nextNextMsg));
            }
            this.logger.debug(`getMatchingCheckResponses: starting prefetch pipeline for next page (${nextMatches.length} IDs)`);
            return runDataPipeline(nextMatches);
          });
        }

        // Process THIS page: either await the prefetched pipeline or run inline.
        if (myDataPromise) {
          // Data pipeline already in-flight from the previous iteration — just await it.
          const finishedMatches = await myDataPromise;
          const matchesToCopy = finishedMatches ?? pageMatches; // fall back to pageMatches if prefetch returned null
          if (!finishedMatches) {
            // Prefetch pipeline was null (ID fetch failed); run inline as fallback.
            this.logger.debug(`getMatchingCheckResponses: page ${pageNum} — prefetch unavailable, running inline`);
            await runDataPipeline(pageMatches);
          }
          each(matchesToCopy, item => {
            if (has(this.responses.data, item.responseID)) {
              responses[item.responseID] = this.responses.data[item.responseID];
            } else {
              this.logger.error(`item ${item.responseID} is not in memory after prefetch pipeline`);
            }
          });
        } else {
          // Page 1 (or fallback): run the data pipeline inline.
          await runDataPipeline(pageMatches);
          each(pageMatches, item => {
            if (has(this.responses.data, item.responseID)) {
              responses[item.responseID] = this.responses.data[item.responseID];
            } else {
              this.logger.error(`item ${item.responseID} is not in memory but it should be`);
            }
          });
        }

        if (isLastPage) {
          complete = true;
        }
      } else {
        this.logger.error(`getMatchingCheckResponses: unexpected status ${data?.reqStatus}`);
        throw new Error(`getResponseIDs returned status: ${data?.reqStatus}`);
      }
    }

    this.logger.debug(`getMatchingCheckResponses: complete — ${pageNum} pages, ${Object.keys(responses).length} responses assembled`);
    this.reportProgress$.next(null);
    return this.buildClasses(responses);
  }

  public cancelCurrentReport(): void {
    this.logger.debug(`cancelCurrentReport: superseding request ${this.getMatchingCallId}`);
    ++this.getMatchingCallId;
    this.reportProgress$.next(null);
    this.reportIntervalCache = null;
  }

  /**
   * Subscribe to reportProgress$ with the standard progress-overlay behaviour used by
   * both the report view page and the report detail page.  Returns the Subscription so
   * the caller can unsubscribe in ngOnDestroy.
   *
   * @param onProgress  callback invoked with each non-null IReportProgress value
   * @param onClear     callback invoked when progress resets to null (loading complete)
   */
  public createProgressSubscription(
    onProgress: (progress: IReportProgress, isFirstMultiPage: boolean) => void,
    onClear: () => void
  ) {
    let isCheckReportLoading = false;
    return this.reportProgress$.subscribe((progress: IReportProgress | null) => {
      if (progress) {
        const isFirstMultiPage = progress.hasMore && !isCheckReportLoading;
        if (isFirstMultiPage) {
          isCheckReportLoading = true;
        }
        if (isCheckReportLoading) {
          onProgress(progress, isFirstMultiPage);
        }
      } else {
        isCheckReportLoading = false;
        onClear();
      }
    });
  }

  private async getResponsesByRow(rows: number[], updateCache: boolean = true ): Promise<number[]> {
    const theList = clone(rows).sort((a,b) => b - a);
    const req = {
      cmd: 'getAvailableResponses',
      trim: 1,
      rows: theList,
      useUpdateTime: 1
    };

    const [data, err] = await awaitHandler(this.chunkedLoad(req, updateCache));

    const retrieved = [];
    if (data && data.reqStatus === 'OK') {
      if (!this.responses.data) {
        this.responses.data = {};
      }
      each(data.result.responses, item => {
        retrieved.push(item.rowID);
        this.responses.data[item.responseID] = item;
      });
    } else if (err) {
      this.logger.error(`getAvailableResponses request failed: ${data.reqStatus}`);
      throw new Error(err);
    }

    // when we are all done, return a list of the rows actually retrieved

    return retrieved;
  }

  private async loadFromCache(responses: { responseID: string; updatedAt: number; row: number }[]): Promise<any[]> {
    const list = [];
     each(responses, item => {
      list.push(item.responseID);
    });

    let matchCount = 0;
    this.logger.debug('attempting restore from localdb');
    let tRef;
    try {
      tRef = this.localdb.table('responses');
    } catch (e) {
      this.logger.error(`localdb not available for response cache: ${e}`);
      return responses;
    }
    const fromCache = await tRef.where('responseID').anyOf(list).toArray(); // bulkGet(list);
    this.logger.debug('fetch from localdb complete');
    if (!fromCache.length) {
      // nothing matched
      this.logger.debug(`no matches in cache`);
      return responses;
    }

    const missed = [];
    if (!this.responses.data) {
      this.responses.data = {};
    }
    const byID = keyBy(fromCache, 'responseID');
    each(responses, item => {
      if (byID[item.responseID] && byID[item.responseID].updatedAt >= item.updatedAt) {
        this.responses.data[item.responseID] = byID[item.responseID];
        matchCount++;
      } else {
        missed.push(item);
      }
    });

    this.logger.debug(`matched ${matchCount}; missed ${missed.length}`);
    return missed;
  }

  // @@@@@@@@@ making everything async
  public async getAllCheckResponses(range, teams: any = null, dontFilter: boolean = false) {
    const startTime = this.utils.toSeconds(range.startTime);
    // make sure all in range are loaded
    const filter = {
      locations: this.userDataService.locations,
      skipFindAvailableTime: 1,
      startTime,
      endTime: 2000000000
    };

    const items = await this.getMatchingCheckResponses(filter);
    if (dontFilter) {
      return items;
    } else {
      const filteredResponses = this.filterResponsesByDate(items, range.startTime, range.endTime);
      if (!teams) {
        return filteredResponses;
      } else {
        return this._filterResponsesByVisibility(filteredResponses, teams);
      }
    }
  }

  public async getAvailableChecksResponses(range, teams, filter = {}) {
    if (range.startTime && range.endTime) {
      return [];
    }

    const availableResponses = await this.getResponses(filter, false);

    return this._filterResponsesByVisibility(availableResponses, teams);
  }

  public async getCheckResponseByCheckIDStatus(checkType: number, range, teams: any, targetSignature: string, status?: string, targetResponses?: Response[]) {
    const s = this.utils.toSeconds(range.startTime);
    const e = this.utils.toSeconds(range.endTime);
    const responses: Response[] = targetResponses || values(await this.getMatchingCheckResponses({
      startTime: s,
      endTime: e || 2000000000,
      skipFindAvailableTime: 1,
      teams,
      signatures: [targetSignature]
    }));

    // this.fillResponses(responses);
    if (!status) {
      // first filter by teams
      const filtered = this._filterResponsesByVisibility(filter(responses, {checkType, targetSignature}), teams);

      if (s && e) { // this means yesterday
        const yesterdayFilter = filter(filtered, (data: any) => {
          if (data.status === 'available' || data.status === 'inProgress' || data.status === 'pending') {
            return false;
          }
          //check if completed
          if (data.completionTime) {
            if (data.completionTime > s && data.completionTime < e) {
              return true;
            } // did it expire on its own?
          } else if (data.expiresTime > s && data.expiresTime < e) {
            return true;
          }
        });
        return yesterdayFilter;
      } else if (s && !e) {
        const timeFiltered = filter(filtered, (data: any) => {
          if (data.status === 'available') {
            return true;
          } else {
            if (data.completionTime > s) {
              return true;
            } else if (data.expiresTime > s) {
              return true;
            }
          }
        });
        return timeFiltered;
      } else {
        return filtered;
      }
    } else {

      const filtered = this._filterResponsesByVisibility(filter(responses, {
        checkType,
        status,
        targetSignature
      }), teams);
      if (s && e) { // this means yesterday
        const yesterdayFilter = filter(filtered, (data: any) => {
          //check if completed
          if (data.completionTime) {
            if (data.completionTime > s && data.completionTime < e) {
              return true;
            } // did it expire on its own?
          } else if (data.expiresTime > s && data.expiresTime < e) {
            return true;
          }
        });
        return yesterdayFilter;
      } else if (s && !e) {
        const timeFiltered = filter(filtered, (data: any) => {
          if (data.completionTime > s) {
            return true;
          } else if (data.expiresTime > s) {
            return true;
          }
        });
        return timeFiltered;
      } else {
        return filtered;
      }
    }
  }

  public checkResponse(ref, opts) {
    const type = ref.type;
    const target = ref.target;
    const assignedTo = ref.assignedTo;
    const owner = ref.owner;
    // ownerData is empty if there is no owner
    const ownerData: any = owner ? this.accountsService.getAccount(ref.owner) : {};

    if (!opts.includingAll) {
      const primary = opts.primary;
      const secondary = opts.secondary;
      const observationCount = includes(opts.extraColumns, 'observationCount');
      const issueCount = includes(opts.extraColumns, 'issueCount');
      const includingList: string[] = ['responder', 'responderTeam', 'responder_shift'];
      if (includes(includingList, primary) || includes(includingList, secondary)) {
        if (!owner) {
          // this has no owner / responder and we are organizing by responder, so bail out
          return false;
        }
      }
      if (observationCount && !get(ref, 'observationCount') || issueCount && !get(ref, 'issueCount')) {
        return false;
      }
    }
    if (opts.obstype && opts.obstype.length) {
      if (!includes(opts.obstype, type)) {
        return false;
      }
    }

    if (opts.checkStatus && opts.checkStatus.length) {
      if (!includes(opts.checkStatus, ref.instanceStatus || ref.status)) {
        return false;
      }
    }

    if (opts.checkType && opts.checkType.length) {
      if (has(ref, 'checkType')) {
        if (!includes(opts.checkType, ref.checkType.toString())) {
          return false;
        }
      }
    }

    if (opts.checkName && opts.checkName.length) {
      if (!includes(opts.checkName, ref.checkID)) {
        return false;
      }
    }

    if (opts.checkDetailName && opts.checkDetailName.length) {
      if (!includes(opts.checkDetailName, ref.checkID)) {
        return false;
      }
    }

    if (opts.deploymentType && opts.deploymentType.length) {
      if (!includes(opts.deploymentType, target.targetType)) {
        return false;
      }
    }

    if (opts.deploymentName && opts.deploymentName.length) {
      if (!includes(opts.deploymentName, ref.deploymentID)) {
        return false;
      }
    }

    if (opts.targetLocations && opts.targetLocations.length) {
      const targetTypes = opts.targetLocations.map(i => Number(i));
      if (ref.locationID) {
        if (indexOf(targetTypes, ref.locationID) === -1) {
          return false;
        }
      } else {
        if (!intersection(targetTypes, target.locations).length) {
          return false;
        }
      }
    }

    if (opts.targetZones && opts.targetZones.length) {
      const targetZones = opts.targetZones.map(i => Number(i));
      if (!intersection(targetZones, target.zones).length) {
        return false;
      }
    }

    if (opts.targetAssetType && opts.targetAssetType.length) {
      const assetTypes = opts.targetAssetType.map(i => Number(i));
      if (!intersection(assetTypes, target.assetTypes).length) {
        return false;
      }
    }

    if (opts.targetAssets && opts.targetAssets.length) {
      const [target, assetID] = split(ref.targetSignature, ':');

      if (target === 'asset') {
        const assetFolderTree = this.foldersDataService.getFolderTreeBy(FolderDataType.ASSET);
        const refFolders: number[] = flattenDeep(ref.target?.folders);

        const isMatch = some(opts.targetAssets, (assetFolder) => {
          if (assetFolder.assetID) {
            return +assetID === assetFolder.assetID;
          } else {
            return this.foldersDataService.hasInFolders(assetFolder.folderID, refFolders, assetFolderTree);
          }
        });

        if (!isMatch) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.targetTeams && opts.targetTeams.length) {
      const targetTeams = opts.targetTeams.map(i => Number(i));
      const targetSignature: string = ref.targetSignature;
      let userID = null;
      let user = null;
      let groups = [];
      if (targetSignature) {
        const splitSig = split(targetSignature, ':');
        // eslint-disable-next-line eqeqeq
        if (splitSig[0] == 'worker') {
          userID = +splitSig[1];
          if (userID) {
            user = this.accountsService.getByID(userID);
            groups = user ? user.groups : [];
          }
        }
      }
      if (userID && user && groups.length) {
        if (!intersection(targetTeams, groups).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.targetPermissions && opts.targetPermissions.length) {
      if (!intersection(opts.targetPermissions, target.permissions).length) {
        return false;
      }
    }

    if (opts.targetUsers && opts.targetUsers.length) {
      const targetUsers = opts.targetUsers.map(i => Number(i));
      const targetSignature: string = ref.targetSignature;
      let userID = null;
      if (targetSignature) {
        const splitSig = split(targetSignature, ':');
        if (splitSig[0] == 'worker') {
          userID = +splitSig[1];
        }
      }
      if (userID) {
        if (!includes(targetUsers, userID)) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.users && opts.users.length && ref.assignedTo.users && ref.assignedTo.users.length) {
      if (assignedTo.users && assignedTo.users.length) {
        if (!intersection(opts.users, assignedTo.users).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.assigned && opts.assigned.length) {
      if (assignedTo.users && assignedTo.users.length) {
        if (assignedTo.users[0] === -1) {
          const splitSig = split(ref.targetSignature, ':');
          if (+splitSig[1]) {
            assignedTo.users[0] = +splitSig[1];
          }
        } else if (assignedTo.users[0] === -2) {
          const splitSig = split(ref.targetSignature, ':');
          const userObj = this.accountsService.getAccount(+splitSig[1]);
          if (userObj.supervisorID) {
            assignedTo.users[0] = userObj.supervisorID;
          }
        }
        if (!intersection(opts.assigned, assignedTo.users).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.assignedTeam && opts.assignedTeam.length) {
      if (assignedTo.teams && assignedTo.teams.length) {
        const assignedTeam = opts.assignedTeam.map(i => Number(i));
        if (!intersection(assignedTeam, assignedTo.teams).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.assignedPerms && opts.assignedPerms.length) {
      if (assignedTo.permissions && assignedTo.permissions.length) {
        if (!intersection(opts.assignedPerms, assignedTo.permissions).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.permissions && opts.permissions.length) {
      if (owner) {
        // there is an owner, so look at that person
        if (!intersection(opts.permissions, Object.keys(ownerData.permissions)).length) {
          return false;
        }
      } else if (ref.assignedTo.permissions && ref.assignedTo.permissions.length) {
        // no owner - are there explicit assignment permissions we can test?
        if (!intersection(opts.permissions, ref.assignedTo.permissions).length) {
          return false;
        }
      }
    }

    if (opts.responders && opts.responders.length) {
      if (owner) {
        const responders = opts.responders.map(i => Number(i));
        if (!includes(responders, owner)) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.groups && opts.groups.length) {
      if (ownerData && ownerData.groups && ownerData.groups.length) {
        if (!intersection(opts.groups, ownerData.groups).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.detailTags && opts.detailTags.length) {
      let detailTags = [];
      if (ref.questionsObjs && ref.questionsObjs.length) {
        forEach(ref.questionsObjs, (qObj, counter) => {
          if (qObj && qObj.closingTags && qObj.closingTags.length) {
            detailTags = [
              ...detailTags,
              qObj.closingTags
            ];
          }
        });
      }
      if (!intersection(opts.detailTags, detailTags).length) {
        return false;
      }
    }

    if (opts.questionCategory && opts.questionCategory.length) {
      const qCategory = [];
      if (ref.questionsObjs && ref.questionsObjs.length) {
        forEach(ref.questionsObjs, (qObj, counter) => {
          if (qObj && qObj.closingCategory) {
            qCategory.push(qObj.closingCategory);
          }
        });
      }
      if (!intersection(opts.questionCategory, qCategory).length) {
        return false;
      }
    }


    return true;
  }

  public async getResponses(filters: IObjectStringKeyMap<any>, useCache: boolean = false, applyFilters: boolean = false, returnResponses: boolean = true): Promise<IResponseCollection> {
    const e = clone(filters);
    e.cmd = e?.changesOnly ? 'getAvailableResponses' : 'getResponses';
    e.noCache = 1;
    e.trim = 1;
    e.sendTime = Date.now();
    if (!has(e, 'status')) {
      e.status = JSON.stringify(['available']);
    }
    if (e.startTime) {
      e.startTime = this.utils.toSeconds(e.startTime);
    }
    if (e.endTime) {
      e.endTime = this.utils.toSeconds(e.endTime);
    }
    if (!e?.changesOnly && useCache && e.startTime && (!this.responseCacheData.startTime || e.startTime < this.responseCacheData.startTime) ) {
      // see if there is anything local we can use
      const theResult = this.upToDate ? await this.restoreFromLocalDB(e.startTime) : await this.restoreAndUpdate(e.startTime);  // try to do a restore
      if (!theResult) {
        this.logger.debug(`nothing to restore from local storage`);
      } else {
        this.logger.debug('restored from local storage');
      }
    }
    if (!e?.changesOnly && useCache && this.responses.data) {
      // do we need to fetch stuff from the backend?
      if (!e.lastRequest && e.startTime && this.responseCacheData.startTime) {
        if (this.responseCacheData.startTime <= e.startTime) {
          // the data is already here
          this.logger.debug('using cached responses');
          if (!returnResponses) {
            return;
          } else {
            return this.buildClasses(this.responses.data, filters, applyFilters);
          }
        } else {
          // only retrieve up to here - we have the rest
          e.endTime = this.responseCacheData.startTime;
          this.logger.debug(`using startTime of ${e.startTime} and endTime of ${e.endTime}`);
        }
        //e.omit = JSON.stringify(rowList);
        // e.lastRequest = this.responses.lastRequest;
      }
    }
    // we got this far and are going to start pulling data
    let data;
    let err;
    if (useCache) {
      this.refreshing = true;
    }
    if (!has(e, 'lastRequest')) {
      // chunked load will deal with cachevalid
      [data, err] = await awaitHandler(this.chunkedLoad(e, useCache));
    } else {
      [data, err] = await awaitHandler(this.commsService.sendMessage(e));
      // roll this up into an array
      if (useCache && has(data.result, 'removed')) {
        if (has(data.result.removed, 'abandoned')) {
          each(data.result.removed.abandoned, (item) => {
            delete this.responses.data[item];
          });
          this.deleteFromLocalDB(data.result.removed.abandoned);
        }
        if (has(data.result.removed, 'canceled')) {
          each(data.result.removed.canceled, (item) => {
            delete this.responses.data[item];
          });
          this.deleteFromLocalDB(data.result.removed.canceled);
        }
      }
      if (useCache && data.result?.responses) {
        // save the changes
        this.updateLocalDB(data.result.responses, { newest: data.result.timestamp, oldest: e?.startTime}, true);
      }
    }
    if (data.reqStatus === 'OK') {
      // some data was loaded
      const rows: IObjectStringKeyMap<IResponse> = data.result.responses;
      if (useCache) {
        this.responses.lastRequest = data.result.timestamp;
        if (!this.responses.data) {
          this.responses.data = {};
        }
        // update the rows with the new data
        each(rows, item => {
          this.responses.data[item.responseID] = item;
        });
        if (e.startTime) {
          // the cache now goes back this far
          this.responseCacheData.startTime = this.utils.toSeconds(e.startTime);
          // was there an endTime?
          // ensure there is a timestamp update in the queue
          let endTime = e?.endTime;
          if (!endTime) {
            endTime = data.result.timestamp;
          } else if (endTime > data.result.timestamp) {
            endTime = data.result.timestamp;
          }

          this.updateLocalDB({}, { newest: endTime, oldest: e.startTime}, true);
        }
        this.events.publish('ccs:checkResponseUpdate', true);
        this.refreshing = false;
        if (returnResponses) {
          return this.buildClasses(this.responses.data, filters);
        } else {
          return;
        }
      } else {
        if (returnResponses) {
          return this.buildClasses(rows, filters);
        } else {
          return;
        }
      }
    } else if (useCache && data && data.reqStatus === 'CACHEVALID') {
      this.logger.debug('Response fetch returned CACHEVALID');
      this.refreshing = false;
      if (returnResponses) {
        return this.buildClasses(this.responses.data, filters);
      } else {
        return;
      }
    } else if (err) {
      this.logger.error(`getAvailableResponses request failed: ${data.reqStatus}`);
      this.refreshing = false;
      throw new Error(err);
    }
    return;
  }

  private buildClasses(rows: IObjectStringKeyMap<IResponse>, filters?: any, applyFilters: boolean = false): IResponseCollection {
    const collection: IResponseCollection = {};
    const depCache = {};
    const checkCache = {};

    // this.logger.debug(`starting to build Classes for ${Object.keys(rows).length} responses`);

      each(rows, (item, responseID) => {
        const deployment = depCache[item.deploymentID] ?? ( depCache[item.deploymentID] = this.deployment.getDeployment(item.deploymentID) );
        if (!deployment) {
          return;
        }
        const check = checkCache[deployment.checkID] ?? ( checkCache[deployment.checkID] = this.checkService.getCheck(deployment.checkID) || {} as ICheck );
        if (filters && applyFilters) {
          const r = new Response(item, check, deployment);
          if (this.testResponse(r, filters)) {
            collection[responseID] = r;
          }
        } else {
          collection[responseID] = new Response(item, check, deployment);
        }
      });
    // this.logger.debug(`Class build complete`);
    return collection;
  }

  private testResponse(ref: Response, opts: any): boolean {
    const owner = ref.owner;
    // ownerData is empty if there is no owner
    const ownerData: any = owner ? this.accountsService.getAccount(ref.owner) : {};

    if (opts.range || opts.startTime) {
      const importantTime = ref.completionTime || ref.expiresTime;
      if (importantTime < ( opts?.startTime ?? opts.range.startTime ) ) {
        return false;
      }
      if (opts.range.endTime || opts.endTime) {
        if (importantTime > ( opts?.endTime ?? opts.range.endTime)) {
          return false;
        }
      }
    }
    if (opts.status && opts.status.length) {
      if (!includes(opts.status, ref.status)) {
        return false;
      }
    }
    if (opts.results && opts.results.length) {
      if (!includes(opts.results, ref.result)) {
        return false;
      }
    }

    if (opts.checkType && opts.checkType.length) {
      if (has(ref, 'checkType')) {
        if (!includes(opts.checkType, ref.checkType.toString())) {
          return false;
        }
      }
    }

    if (opts.checkName && opts.checkName.length) {
      if (!includes(opts.checkName, ref.checkID)) {
        return false;
      }
    }

    if (opts.checkDetailName && opts.checkDetailName.length) {
      if (!includes(opts.checkDetailName, ref.checkID)) {
        return false;
      }
    }

    const target = ref.deployment.target;
    if (opts.deploymentType && opts.deploymentType.length) {
      if (!includes(opts.deploymentType, target.targetType)) {
        return false;
      }
    }

    if (opts.deploymentName && opts.deploymentName.length) {
      if (!includes(opts.deploymentName, ref.deploymentID)) {
        return false;
      }
    }

    if (opts.targetLocations && opts.targetLocations.length) {
      const targetLocations = opts.targetLocations.map(i => Number(i));
        if (!intersection(targetLocations, ref.targetLocations).length) {
          return false;
        }
    }

    if (opts.targetZones && opts.targetZones.length) {
      const targetZones = opts.targetZones.map(i => Number(i));
      if (!intersection(targetZones, ref.targetZones).length) {
        return false;
      }
    }

    if (opts.targetAssetType && opts.targetAssetType.length) {
      const assetTypes = opts.targetAssetType.map(i => Number(i));
      if (!intersection(assetTypes, target.assetTypes).length) {
        return false;
      }
    }

    if (opts.targetAssets && opts.targetAssets.length) {
      const assets = opts.targetAssets.map(i => Number(i));
      const targetSignature: string = ref.targetSignature;
      let assetsID = null;
      if (targetSignature) {
        const splitSig = split(targetSignature, ':');
        if (splitSig[0] == 'asset') {
          assetsID = +splitSig[1];
        }
      }
      if (assetsID) {
        if (!includes(assets, assetsID)) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.targetTeams && opts.targetTeams.length) {
      const targetTeams = opts.targetTeams.map(i => Number(i));
      const targetSignature: string = ref.targetSignature;
      let userID = null;
      let user = null;
      let groups = [];
      if (targetSignature) {
        const splitSig = split(targetSignature, ':');
        // eslint-disable-next-line eqeqeq
        if (splitSig[0] == 'worker') {
          userID = +splitSig[1];
          if (userID) {
            user = this.accountsService.getByID(userID);
            groups = user ? user.groups : [];
          }
        }
      }
      if (userID && user && groups.length) {
        if (!intersection(targetTeams, groups).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.targetPermissions && opts.targetPermissions.length) {
      if (!intersection(opts.targetPermissions, target.permissions).length) {
        return false;
      }
    }

    if (opts.targetUsers && opts.targetUsers.length) {
      const targetUsers = opts.targetUsers.map(i => Number(i));
      const targetSignature: string = ref.targetSignature;
      let userID = null;
      if (targetSignature) {
        const splitSig = split(targetSignature, ':');
        if (splitSig[0] == 'worker') {
          userID = +splitSig[1];
        }
      }
      if (userID) {
        if (!includes(targetUsers, userID)) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.users && opts.users.length && ref.assignedTo.users && ref.assignedTo.users.length) {
      if (!intersection(opts.users, ref.assignedTo.users).length) {
        return false;
      }
    }

    if (opts.assigned && opts.assigned.length) {
      if (ref.assignedTo.users && ref.assignedTo.users.length) {
        if (ref.assignedTo.users[0] === -1) {
          const splitSig = split(ref.targetSignature, ':');
          if (+splitSig[1]) {
            ref.assignedTo.users[0] = +splitSig[1];
          }
        } else if (ref.assignedTo.users[0] === -2) {
          const splitSig = split(ref.targetSignature, ':');
          const userObj = this.accountsService.getAccount(+splitSig[1]);
          if (userObj.supervisorID) {
            ref.assignedTo.users[0] = userObj.supervisorID;
          }
        }
        if (!intersection(opts.assigned, ref.assignedTo.users).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.assignedTeam && opts.assignedTeam.length) {
      if (ref.assignedTo.teams && ref.assignedTo.teams.length) {
        const assignedTeam = opts.assignedTeam.map(i => Number(i));
        if (!intersection(assignedTeam, ref.assignedTo.teams).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.assignedPerms && opts.assignedPerms.length) {
      if (ref.assignedTo.permissions && ref.assignedTo.permissions.length) {
        if (!intersection(opts.assignedPerms, ref.assignedTo.permissions).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (opts.permissions && opts.permissions.length) {
      if (owner) {
        // there is an owner, so look at that person
        if (!intersection(opts.permissions, Object.keys(ownerData.permissions)).length) {
          return false;
        }
      } else if (ref.assignedTo.permissions && ref.assignedTo.permissions.length) {
        // no owner - are there explicit assignment permissions we can test?
        if (!intersection(opts.permissions, ref.assignedTo.permissions).length) {
          return false;
        }
      }
    }

    if (opts.groups && opts.groups.length) {
      if (ownerData && ownerData.groups && ownerData.groups.length) {
        if (!intersection(opts.groups, ownerData.groups).length) {
          return false;
        }
      } else {
        return false;
      }
    }

    return true;
  }

  getFlagIssue(flagIDArray: number[]): Promise<any> {
    const reqObj = {
      cmd: 'getResponseIssues',
      issues: JSON.stringify(flagIDArray)
    };
    return new Promise((resolve, reject) => {
      this.commsService.sendMessage(reqObj, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve(data.result.issues);
        }
      }).catch((err) => {
        reject(err);
      });
    });
  }

  getTargetZoneName(locId: number, zoneId: number): string {
    const zone = this.userService.findAnyZoneNoLoc(zoneId);

    const location = this.userService.getLocation(locId) || {
      name: this.translate.instant('SHARED.Unknown')
    };

    const zoneTitle = this.translate.instant('SHARED.Zone');

    const zoneWithLocation = toLower(location.name) + ' / ' + toLower(zone.name);

    return `${zoneTitle}: ${zoneWithLocation}`;
  }

  private async chunkedLoad(query: any, useCache: boolean = false): Promise<any> {
    this.refreshing = true;
    return new Promise(async (resolve, reject) => {
      let lastReq = null;
      let complete = false;
      let lastRow = null;
      const ret = {
        reqStatus: null,
        result: {
          timestamp: null,
          responses: null,
        }
      };

      while (!complete) {
        const e = clone(query);
        e.limit = this.chunkSize;
        if (lastRow) {
          e.startAfter = lastRow;
          this.logger.debug('Fetching another chunk of responses');
        } else {
          delete e.startAfter;
          this.logger.debug('Starting a response request');
        }
        const [data, err] = await awaitHandler(this.commsService.sendMessage(e, false, false));
        if (data && data.reqStatus === 'OK') {
          this.logger.debug(`Response fetch completed; retrieved ${data.result.numResponses} responses`);
          // only update the storage on the last fetch
          if (!has(data.result, 'numResponses') || data.result.numResponses === 0) {
            complete = true;
            ret.reqStatus = data.reqStatus;
            // Only set responses from this (empty) terminal page if nothing was accumulated yet.
            // If we already have data, a row-based batch returned exactly chunkSize rows and this
            // is the empty follow-up page — do NOT overwrite the accumulated responses with {}.
            if (!ret.result.responses) {
              ret.result.responses = data.result.responses;
            }
          } else {
            // we loaded some...
            if (data.result.numResponses < this.chunkSize) {
              // we ran out of items to fetch
              complete = true;
              this.initialCacheFill = false;
              // put the last request back to where we started so we can get anything that has been updated since then!
              if (lastReq) {
                ret.result.timestamp = lastReq;
              } else {
                ret.result.timestamp = data.result.timestamp;
              }
              // this.logger.log(`We have fetched all of the available responses; doing a final refresh to get anything changed since ${this.responses.lastRequest}`);
              // await this.refresh(undefined);
            } else {
              lastRow = data.result.lastResponse;
              if (!lastReq) {
                lastReq = data.result.timestamp;
              }
            }
            if (!ret.result.responses) {
              ret.result.responses = data.result.responses;
            } else {
              assign(ret.result.responses, data.result.responses);
            }
            if (!ret.reqStatus) {
              ret.reqStatus = data.reqStatus;
            }
          }
        } else if (data && data.reqStatus === 'CACHEVALID') {
          this.logger.debug('chunked request responded with CACHEVALID');
          ret.reqStatus = 'CACHEVALID';
        }
      }
      // Write all accumulated responses to localdb once after the full load completes,
      // rather than per-chunk, to avoid cascading IDB write locks blocking subsequent reads.
      if (useCache && ret.result.responses && Object.keys(ret.result.responses).length > 0) {
        await this.updateLocalDB(ret.result.responses);
      }
      resolve(ret);
    });
  }

  // private fillResponse(item: Response): Response {
  //     const deployment = this.deployment.getDeployment(item.deploymentID);
  //     const check = this.checkService.getCheck(deployment.checkID) || {} as ICheck;

  //     item = defaults(item, deployment);
  //     item.checkID = check.checkID;
  //     item.checkType = check.checkType;
  //     item.deploymentAssignedTo = deployment.assignedTo;
  //     return item;
  // }

  // private fillResponses(responses: Response[]): Response[] {
  //   const depCache = {};
  //   const checkCache = {};
  //   each(responses, item => {
  //     const deployment = depCache[item.deploymentID] ?? ( depCache[item.deploymentID] = this.deployment.getDeployment(item.deploymentID) );
  //     const check = checkCache[deployment.checkID] ?? ( checkCache[deployment.checkID] = this.checkService.getCheck(deployment.checkID) || {} as ICheck );

  //     item = defaults(item, deployment);
  //     item.checkID = check.checkID;
  //     item.checkType = check.checkType;
  //     item.deploymentAssignedTo = deployment.assignedTo;
  //   });
  //   return responses;
  // }

  private findRespItemByObsID(oid: number) {
    let respItemRef = null;
    if (!isEmpty(this.responseItemCache[oid])) {
      return this.responseItemCache[oid];
    }
    each(this.responses.data, (ref: any) => {
      if (!isEmpty(ref.answers)) {
        const r: any = find(ref.answers, {observationID: oid});
        if (r) {
          r.responseID = ref.responseID;
          respItemRef = r;
          return false;
        }
      }
    });
    if (respItemRef) {
      this.responseItemCache[oid] = respItemRef;
    }
    return respItemRef;
  }

  private filterResponsesByDate(responses, startTime: number, endTime: number) {
    let filteredResponses;
    const s = this.utils.toSeconds(startTime);
    const e = this.utils.toSeconds(endTime);

    if (s && e) { // this means yesterday
      filteredResponses = filter(responses, (data: any) => {
        if (data.status === 'available' || data.status === 'inProgress' || data.status === 'pending') {
          return false;
        }
        //check if completed
        if (data.completionTime) {
          if (data.completionTime > s && data.completionTime < e) {
            return true;
          } // did it expire on its own?
        } else if (data.expiresTime > s && data.expiresTime < e) {
          return true;
        }
      });
    } else if (s && !e) {
      filteredResponses = filter(responses, (data: any) => {
        if (data.status === 'available') {
          return true;
        } else {
          if (data.completionTime > s) {
            return true;
          } else if (data.expiresTime > s) {
            return true;
          }
        }
      });
    } else {
      filteredResponses = responses;
    }

    return filteredResponses;
  }

  private _filterResponsesByVisibility(collection, teams) {
    // CVX-6285: no team scope means "do not scope by team", not "no team
    // matches". Without this, teamIds stays undefined below and
    // intersection(assignedTeams, undefined) is [], so every response whose
    // deployment is assigned to any team is silently dropped and the caller
    // gets an empty list.
    //
    // Reachable from exactly ONE path: getCheckResponseByCheckIDStatus, which the
    // asset-detail drill-down enters with currentTeam = null. getAllCheckResponses
    // does NOT reach it -- it has its own `!teams` short-circuit at :730 and never
    // calls in with a falsy argument -- and getAvailableChecksResponses's only
    // caller always passes a truthy object. (QA round 3 corrected an earlier
    // version of this comment that claimed more than one entry point.)
    if (!teams) {
      return collection;
    }

    const currentUser = this.accountsService.getAccount(this.userDataService.userID);
    const teamIds: number[] = teams.currentId === -1 ? teams.ids : [teams.currentId];

    // CVX-6285 QA round 2: do NOT add an "empty teamIds means no scoping" early return
    // here. It was tried in 997f47234 and reverted. `{ids: [], currentId: -1}` is
    // deliberately produced by supervisor-dashboard.page.ts:274 and accepted by
    // check-status.component.ts:307, so an empty list is a REACHABLE dashboard state,
    // not just the drill-down's missing argument -- and returning early there widens
    // what an unteamed supervisor sees, bypassing all four rules below rather than
    // only the team-assignment one.
    //
    // The premise that made it look safe ("this is client-side scoping, the server
    // already scoped these rows") was tested against getResponseIDs.cgi and is FALSE:
    // that path applies no team or assignment scoping, so this filter is the only
    // implementation of the rule. The `!teams` guard above is what CVX-6285 needs --
    // the drill-down passes currentTeam = null, which it already covers.

    const getAccount = memoize(id => this.accountsService.getAccount(id));

    return filter(collection, (data: any) => {
      // 1. is the deploymentAssignedTo specific to a team?  If yes, then skip ones that are not assigned to my selected teams

      const intArry = map(data.deploymentAssignedTo.teams, Number);
      if (intArry.length && !intersection(intArry, teamIds).length) {
        return false;
      }

      // 2. is the deploymentAssignedTo specific to individual users (or to self, which means it is the same as the target worker)?  If yes, determine their primary teams and check the intersection of those teams with my selected teams.  Skip if the intersection set is empty (no overlapping teams)

      const t = split(data.targetSignature, ':');
      const targetUser = t[0] === 'worker' ? getAccount(+t[1]) : null;

      if (has(data.deploymentAssignedTo, 'users') && data.deploymentAssignedTo.users.length) {
        let match = 0;
        each(data.deploymentAssignedTo.users, user => {
          if (user && user > -2) {
            // user -1 means assignee is the target user
            const uObj = (user === -1) ? targetUser : getAccount(+user);
            if (+user === currentUser.userID) {
              match = 1;
            } else if (uObj && uObj.primaryGroup) {
              if (indexOf(teamIds, uObj.primaryGroup) > -1) {
                // this is assigned to someone on a team we are watching
                // am I an admin? If so then we should always show the info
                if (this.permissions.canView(Permission.Admin)) {
                  match = 1;
                // am I THEIR supervisor
                // 2.1. if it is an overlapping team, and I am a supervisor, am I the supervisor for any of the individual users?  If not, then skip.
                } else if (uObj && uObj.supervisorID) {
                  if (uObj.supervisorID === currentUser.userID) {
                    match = 1;
                  }
                } else {
                  // they dont have a supervisor and are on a team I am looking at
                  match = 1;
                }
              }
            } else if (uObj) {
              // they have no primary group; am I their supervisor?
              if (uObj && uObj.supervisorID) {
                if (uObj.supervisorID === currentUser.userID) {
                  match = 1;
                }
              }
            }
          } else if (user === -2) {
            // it is assigned to a supervisor.  is this person on a team we are watching?
            // 4. is the target a worker and does the deploymentAssignedTo users property include -2 (supervisor).  If so, and if I am not that user's supervisor, skip.
            if (targetUser && targetUser.primaryGroup && indexOf(teamIds, targetUser.primaryGroup) > -1) {
              if (targetUser.supervisorID && targetUser.supervisorID === currentUser.userID) {
                match = 1;
              }
            }
          }
        });

        if (!match) {
          return false;
        }
      }

      // 3. is the target a worker?   If so, then what is that worker's primary team?   Skip if that team is not one of my selected teams
      if (t[0] === 'worker' && targetUser && targetUser.primaryGroup && indexOf(teamIds, targetUser.primaryGroup) < 0) {
        return false;
      }
      // 5. If we get this far, this is one of my team's relevant available responses.  Show it.

      // if it is "All teams" then we should check "checks" that have empty deploymentAssignedTo
      /* if (teams.currentId === -1) {
        if (!intArry.length) {
          if (data.status === 'available') {
            return true;
          }
        }
      } */

      /* if (intArry.length) {
        const matchesTeam = intersection(teamIds, intArry).length
        if (matchesTeam) {
          // now check for status
          if (data.status === 'available') {
            return true;
          }
        }
      } */
      return true;
    });
  }

  private deleteFromLocalDB(items: string[]): Promise<void> {
    const tRef = this.localdb.table('responses');
    if (tRef) {
      this.logger.debug(`deleting ${items.length} records from responses`);
      return tRef.bulkDelete(items).then(async (res) => {
        const count = await tRef.count();
        this.logger.debug(`localdb responses now has ${count} records`);
      });
    } else {
      return;
    }
  }

  private updateCounter = 0;
  private isUpdating = false;
  private updateQueue = [];
  private localCacheDisabled = false;

  private async updateLocalDB(records: IObjectStringKeyMap<IResponse>, times?: { newest: number; oldest: number }, updateTimes?: boolean): Promise<boolean> {
    	if (this.localCacheDisabled) {
        return false;
      }
    	if (!updateTimes && Object.keys(records).length === 0) {
        this.logger.debug('There are no records in the update');
        return false;
      }

      this.updateQueue.push({
        records, times, updateTimes
      });

      this.logger.debug(`added update to queue: ${this.updateQueue.length}`);
      if (this.isUpdating) {
        return true;
      }
      this.isUpdating = true;
      this.queueRunner();
      return true;
    }

  private async queueRunner(): Promise<void> {
    // Sub-batch size for IDB writes. Large writes (e.g. 50k records) hold an exclusive
    // IDB transaction lock for 37+ seconds, blocking all localdb reads. Splitting into
    // smaller batches keeps individual lock durations short so reads can interleave.
    const localDbWriteBatchSize = 5000;

    while (this.updateQueue.length) {
      const job = this.updateQueue.shift();
      const times = job.times;

      // Merge stored times once per job (not per sub-batch).
      let mergedTimes = null;
      if (times) {
        const [storedTimes] = await awaitHandler(this.localdb.table('updateTimes').get('responses'));
        if (storedTimes) {
          if (!times?.oldest || (has(storedTimes, 'oldest') && storedTimes.oldest < times.oldest)) {
            times.oldest = storedTimes.oldest;
          }
          if (!times?.newest || (has(storedTimes, 'newest') && storedTimes.newest > times.newest)) {
            times.newest = storedTimes.newest;
          }
        }
        if (times.newest && times.oldest) {
          mergedTimes = times;
          this.logger.debug(`Times are ${times.oldest}, ${times.newest}`);
        }
      }

      const keys = Object.keys(job.records);
      // When records is empty (times-only update), run a single pass with an empty batch.
      const totalBatches = keys.length === 0 ? 1 : Math.ceil(keys.length / localDbWriteBatchSize);
      let writeError = false;

      for (let batchIdx = 0; batchIdx < totalBatches; batchIdx++) {
        const isLastBatch = batchIdx === totalBatches - 1;

        // Build sub-batch data object.
        const batchData: IObjectStringKeyMap<IResponse> = {};
        if (keys.length > 0) {
          const start = batchIdx * localDbWriteBatchSize;
          const end = Math.min(start + localDbWriteBatchSize, keys.length);
          for (let k = start; k < end; k++) {
            batchData[keys[k]] = job.records[keys[k]];
          }
        }

        const upArgs: IDBUpdate = {
          table: 'responses',
          data: batchData,
          dataIsArray: false
        };

        // Apply times update only on the last sub-batch so it reflects the full write.
        if (isLastBatch && mergedTimes) {
          upArgs.times = mergedTimes;
          upArgs.updateTimes = true;
        }

        const updateNum = ++this.updateCounter;
        const batchDesc = totalBatches > 1 ? ` (${batchIdx + 1}/${totalBatches})` : '';
        this.logger.debug(`starting localdb responses update #${updateNum}${batchDesc} with ${Object.keys(batchData).length} items`);
        const [result, err] = await awaitHandler(this.webworkers.updateDB(upArgs));
        if (result && result.recovered) {
          this.logger.error(`localdb recovery: cleared table '${result.clearedTable}' after QuotaExceededError on responses update #${updateNum} (CVX-5918) — table was purged and refilled; monitor for repeat occurrences.`);
          // Invalidate updateTimes so restoreFromLocalDB triggers a server-side
          // backfill for the records the clear() destroyed (CVX-5918).
          await awaitHandler(this.localdb.table('updateTimes').delete(result.clearedTable));
          // CVX-5918 F-02/F-03: stop processing remaining batches of this job.
          // clear() destroyed earlier batches' rows, and updateTimes is now gone,
          // so writing further batches into the cleared table would consume
          // quota for data that restoreFromLocalDB will refuse to serve. Also
          // prevents emitting one Sentry event per sub-batch on a repeat-quota
          // scenario. The outer while-loop continues with the next queued job.
          break;
        }
        if (err) {
          const isQuota = (err && (err.name === 'QuotaExceededError' || (err.message && err.message.includes('QuotaExceeded'))));
          if (isQuota) {
            this.logger.error('Storage quota exceeded — local response cache DISABLED for this session after table-clear retry failed. Data will be fetched from server. (CVX-5918)');
            this.localCacheDisabled = true;
            // Invalidate updateTimes so readers fall through to server rather
            // than returning the cleared table's empty state (CVX-5918).
            await awaitHandler(this.localdb.table('updateTimes').delete('responses'));
            this.updateQueue = [];
            this.isUpdating = false;
            return;
          }
          const errMsg = err instanceof Error ? err.message : JSON.stringify(err);
          this.logger.error(`update #${updateNum} of localdb failed: ${errMsg}`);
          this.updateQueue = [];
          writeError = true;
          break;
        }
        this.logger.debug(`finished localdb responses update #${updateNum}`);
      }

      if (writeError) {
        break;
      }
    }
    this.isUpdating = false;
    return;
  }


  private isRestoring = false;

  public async restoreAndUpdate(earliestTime?: number): Promise<boolean> {
    // load whatever we were asked to from cache; this should also reset the lastRequest time
    await this.restoreFromLocalDB(earliestTime);
    this.logger.debug('attempting to refresh from the backend');
    await this.refresh();
    this.upToDate = true;
    if (this.responses?.lastRequest) {
      return true;
    } else {
      return false;
    }
  }

  public async restoreFromLocalDB(earliestTime?: number): Promise<boolean> {
    if (this.isRestoring) {
      return false;
    }
    this.isRestoring = true;
    if (earliestTime) {
      earliestTime = this.utils.toSeconds(earliestTime);
      if (this.responseCacheData?.startTime && this.responseCacheData.startTime < earliestTime) {
        // the data from localcache is already loaded to here
        this.isRestoring = false;
        return true;
      }
    }

    // check if there is any data in there
    let times: IUpdateTime;
    let err = null;
    [times, err] = await awaitHandler( this.localdb.table('updateTimes').get('responses'));

    if (!times || ! times?.oldest) {
      // we don't seem to have any cached data
      this.isRestoring = false;
      return false;
    }

    // we have some cached data; does it go back further than the current request

    if (this.responseCacheData.startTime && times.oldest >= this.responseCacheData.startTime) {
      // we have already loaded everything cached locally
      this.isRestoring = false;
      return false;
    }

    if (times) {
      // okay - there was something
      this.logger.debug('starting restore from localdb');
      const tRef = this.localdb.table('responses');
      if (!this.responses?.data) {
        // ensure there is a bucket
        this.responses.data = {};
      }

      // is there a startTime
      let query;
      if (earliestTime) {
        // possibly amend earliest time so we are not confused about what is in RAM

        const eQuery = await tRef.where('updatedAt').aboveOrEqual(earliestTime).sortBy('availableTime');
        if (eQuery.length) {
          const ordered = sortBy(eQuery, 'availableTime');
          if (ordered[0].availableTime < earliestTime) {
            this.logger.debug(`changing earliestTime to ${ordered[0].availableTime}`);
            earliestTime = ordered[0].availableTime;
          }
        }

        if (this.responseCacheData.startTime) {
          // some are already loaded; we can load between or equal
          this.logger.debug(`loading between ${earliestTime} and ${this.responseCacheData.startTime}`);
          query = tRef.where('availableTime').between(earliestTime, this.responseCacheData.startTime, true, false);
        } else {
          // only fetch some
          this.logger.debug(`loading above or equal to ${earliestTime}`);
          query = tRef.where('availableTime').aboveOrEqual(earliestTime);
        }
      } else {
        this.logger.debug(`loading everything`);
        query = tRef;
      }
      // pull in everything
      let counter = 0;
      await query.toArray(async (theItems) => {
        this.logger.debug(`${theItems.length} items retrieved from localdb`);
        theItems.forEach((item) => {
          this.responses.data[item.responseID] = item;
          counter++;
        });
        return true;
      });

      // const theItems = await this.webworkers.restoreDB('responses');
      // we have either loaded a limited subset or everything
      const loadedTime = (earliestTime && times.oldest < earliestTime) ? earliestTime : times.oldest;
      this.responseCacheData.startTime = loadedTime;
      this.responseCacheData.endTime = times.newest;
      this.responses.lastRequest = times.newest;
      this.isRestoring = false;
      this.logger.debug(`restore of ${counter} responses from localdb complete`);
      this.logger.debug(`There are now ${Object.keys(this.responses.data).length} responses in RAM`);
      return true;
    } else {
      this.isRestoring = false;
      return false;
    }
  }

}
